import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import { candidatesApi, matchesApi, jobsApi, hrPipelineApi, authApi } from '../lib/api';
import { useDataRefresh, DATA_EVENTS } from '../contexts/DataRefreshContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageVisitLogger, logSearch, logFilter } from '../hooks/useActivityLogger';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { StatusBadge } from '../components/common/Badges';
import FileUpload from '../components/common/FileUpload';
import Pagination from '../components/common/Pagination';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import CandidatePeekPanel from '../components/candidates/CandidatePeekPanel';
import BulkUploadDialog from '../components/candidates/BulkUploadDialog';
import AddToRequirementDialog from '../components/requirements/AddToRequirementDialog';
import {
  Plus,
  Search,
  Upload,
  MapPin,
  Mail,
  Phone,
  Briefcase,
  Loader2,
  Eye,
  Trash2,
  CheckSquare,
  X,
  CheckCircle,
  Bot,
  Filter,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
  Clock,
  Calendar,
  DollarSign,
  GraduationCap,
  Building2,
  Sparkles,
  List,
  LayoutGrid,
  RefreshCw,
  Zap,
  Share2,
  Send,
  Globe,
  PanelRightOpen,
  LogIn,
  FolderOpen,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

// Freshness calculation helper
const getFreshness = (createdAt) => {
  if (!createdAt) return { label: 'Unknown', color: 'bg-slate-100 text-slate-600', days: null };
  
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now - created);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return { label: 'Today', color: 'bg-green-100 text-green-700', days: 0 };
  if (diffDays === 1) return { label: 'Yesterday', color: 'bg-green-100 text-green-700', days: 1 };
  if (diffDays <= 7) return { label: `${diffDays}d ago`, color: 'bg-emerald-100 text-emerald-700', days: diffDays };
  if (diffDays <= 30) return { label: `${Math.floor(diffDays / 7)}w ago`, color: 'bg-amber-100 text-amber-700', days: diffDays };
  if (diffDays <= 90) return { label: `${Math.floor(diffDays / 30)}mo ago`, color: 'bg-orange-100 text-orange-700', days: diffDays };
  return { label: `${Math.floor(diffDays / 30)}mo ago`, color: 'bg-red-100 text-red-700', days: diffDays };
};

const PAGE_SIZE_OPTIONS = [10, 20, 40, 80, 160];

export default function CandidatesPage() {
  // Activity logging
  usePageVisitLogger('Candidates');
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { triggerRefresh } = useDataRefresh();
  const { user: currentUser } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Initialize from URL params for back button support
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseInt(searchParams.get('limit')) || 20);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [verificationFilter, setVerificationFilter] = useState(() => searchParams.get('verified') || '');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [addToRequirementOpen, setAddToRequirementOpen] = useState(false); // bulk: selected -> requirements
  const [addToRequirementDone, setAddToRequirementDone] = useState(false); // clear the selection once the dialog closes after an add
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [bulkMatching, setBulkMatching] = useState(false);
  const [bulkParsing, setBulkParsing] = useState(false);
  const [bulkParseStatus, setBulkParseStatus] = useState(null);
  
  // Candidate Portal Actions
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [resetMethod, setResetMethod] = useState('email');
  const [sendingReset, setSendingReset] = useState(false);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  
  // Sync state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (itemsPerPage !== 20) params.set('limit', itemsPerPage);
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (verificationFilter) params.set('verified', verificationFilter);
    setSearchParams(params, { replace: true });
  }, [page, itemsPerPage, search, statusFilter, verificationFilter, setSearchParams]);
  
  // Advanced Search State
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  // Phones only: the quick-filter selects live behind a single "Filters" button.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [minExperience, setMinExperience] = useState('');
  const [maxExperience, setMaxExperience] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [freshnessFilter, setFreshnessFilter] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Match data for all candidates
  const [candidateMatches, setCandidateMatches] = useState({});
  
  // Quick Share state
  const [quickShareOpen, setQuickShareOpen] = useState(false);
  const [quickShareCandidate, setQuickShareCandidate] = useState(null);
  const [quickShareJobs, setQuickShareJobs] = useState([]);
  const [selectedJobsForShare, setSelectedJobsForShare] = useState([]);
  const [hrEmailOverride, setHrEmailOverride] = useState('');
  const [quickSharing, setQuickSharing] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Bulk Quick Share state
  const [bulkShareOpen, setBulkShareOpen] = useState(false);
  const [bulkShareJobs, setBulkShareJobs] = useState([]);
  const [selectedJobForBulkShare, setSelectedJobForBulkShare] = useState(null);
  const [bulkShareHrEmail, setBulkShareHrEmail] = useState('');
  const [bulkShareHrName, setBulkShareHrName] = useState('');
  const [bulkSharing, setBulkSharing] = useState(false);
  const [loadingBulkJobs, setLoadingBulkJobs] = useState(false);

  // Peek Panel state
  const [peekCandidate, setPeekCandidate] = useState(null);
  const [peekOpen, setPeekOpen] = useState(false);

  // Handle opening candidate portal in a new tab
  const handleOpenCandidatePortal = (candidate, e) => {
    e.stopPropagation();
    if (!candidate.portal_slug) {
      toast.error('Candidate has no portal access');
      return;
    }
    window.open(`/c/${candidate.portal_slug}`, '_blank');
  };

  // Handle sending password reset to candidate
  const handleSendCandidatePasswordReset = async () => {
    if (!resetPasswordDialog) return;
    setSendingReset(true);
    try {
      const response = await authApi.sendCandidatePasswordReset(resetPasswordDialog.id, resetMethod);
      toast.success(response.data.message || `Password reset sent via ${resetMethod}`);
      // If reset_link is returned (when email/whatsapp not configured), show it
      if (response.data.reset_link) {
        toast.info(`Reset link: ${response.data.reset_link}`, { duration: 10000 });
      }
      setResetPasswordDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send password reset');
    } finally {
      setSendingReset(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, [page, itemsPerPage, search, statusFilter, verificationFilter, selectedSkills, minExperience, maxExperience, locationFilter, freshnessFilter, sortBy, sortOrder]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Load filter options from DB
  const [filterOptions, setFilterOptions] = useState({ locations: [], skills: [], designations: [], companies: [], industries: [] });
  
  const loadFilterOptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/candidates/filter-options`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
        setAvailableSkills(data.skills || []);
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  // Load matches when candidates change
  useEffect(() => {
    if (candidates.length > 0) {
      loadCandidateMatches();
    }
  }, [candidates]);

  const loadCandidateMatches = async () => {
    try {
      const { matchesApi } = await import('../lib/api');
      const matchData = {};
      
      // Fetch matches for all visible candidates
      await Promise.all(candidates.map(async (candidate) => {
        try {
          const response = await matchesApi.list({ candidate_id: candidate.id, limit: 100 });
          const matches = response.data.matches || [];
          // Normalize score field (API returns overall_score)
          const normalizedMatches = matches.map(m => ({
            ...m,
            score: m.overall_score || m.score || 0,
            job_title: m.job_title || m.job?.job_title || 'Unknown Job',
            company_name: m.company_name || m.job?.company_name || ''
          }));
          
          // Count unique companies from matches above 70%
          const above70 = normalizedMatches.filter(m => m.score >= 70);
          const uniqueCompanies = new Set(above70.map(m => m.company_name).filter(Boolean));
          
          matchData[candidate.id] = {
            all: normalizedMatches,
            above70: above70,
            highest: normalizedMatches.length > 0 ? Math.max(...normalizedMatches.map(m => m.score)) : null,
            totalJdMatches: above70.length,
            uniqueCompanyCount: uniqueCompanies.size
          };
        } catch (e) {
          matchData[candidate.id] = { all: [], above70: [], highest: null, totalJdMatches: 0, uniqueCompanyCount: 0 };
        }
      }));
      
      setCandidateMatches(matchData);
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  };

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const response = await candidatesApi.list({
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        verification_status: verificationFilter || undefined,
        skills: selectedSkills.length > 0 ? selectedSkills.join(',') : undefined,
        min_experience: minExperience || undefined,
        max_experience: maxExperience || undefined,
        location: locationFilter || undefined,
        freshness: freshnessFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setCandidates(response.data.candidates);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === candidates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(candidates.map(c => c.id));
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleUpload = async (file, aiProvider) => {
    try {
      const response = await candidatesApi.uploadCV(file, aiProvider);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      throw error;
    }
  };

  const handleUploadComplete = (queuedCount, errorCount) => {
    setUploadOpen(false);
    if (queuedCount > 0) {
      toast.success(`${queuedCount} CV${queuedCount > 1 ? 's' : ''} queued for processing`);
      triggerRefresh(DATA_EVENTS.CANDIDATE_CREATED, { count: queuedCount });
    }
    loadCandidates();
  };

  const handleDelete = async (candidateId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    
    try {
      await candidatesApi.delete(candidateId);
      toast.success('Candidate deleted');
      triggerRefresh(DATA_EVENTS.CANDIDATE_DELETED, { candidateId });
      loadCandidates();
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} candidates?`)) return;
    
    try {
      await Promise.all(selectedIds.map(id => candidatesApi.delete(id)));
      toast.success(`${selectedIds.length} candidates deleted`);
      setSelectedIds([]);
      triggerRefresh(DATA_EVENTS.CANDIDATE_DELETED);
      loadCandidates();
    } catch (error) {
      toast.error('Failed to delete some candidates');
    }
  };

  const handleBulkPowerMatch = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkMatching(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      // Process candidates sequentially to avoid rate limits
      for (const candidateId of selectedIds) {
        try {
          const response = await fetch(`${API_URL}/api/power-matches/run`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ candidate_id: candidateId })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (e) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Power Match completed for ${successCount} candidate(s)`);
        loadCandidates();
        // Reload match data
        loadMatchData();
      }
      if (errorCount > 0) {
        toast.error(`Failed for ${errorCount} candidate(s)`);
      }
      setSelectedIds([]);
    } catch (error) {
      toast.error('Bulk Power Match failed');
    } finally {
      setBulkMatching(false);
    }
  };

  // Bulk Power Parse function (like bulk upload batch technique)
  const handleBulkPowerParse = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Power Parse ${selectedIds.length} candidate(s)? This will re-parse their CVs using AI and send them a WhatsApp notification.`)) return;
    
    setBulkParsing(true);
    setBulkParseStatus({ is_running: true, total: selectedIds.length, completed: 0, failed: 0, percentage: 0, current_item: null });
    
    try {
      // Start bulk power parse
      const response = await candidatesApi.bulkPowerParse(selectedIds, 'anthropic');
      
      if (response.data.batch_id) {
        toast.success(`Power Parse started for ${selectedIds.length} candidate(s). Running in background...`);
        
        // Poll for status updates every 3 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await candidatesApi.getBulkPowerParseStatus();
            const status = statusResponse.data;
            setBulkParseStatus(status);
            
            // Show progress toast for current item
            if (status.current_item && status.is_running) {
              // Update progress silently - the button shows percentage
            }
            
            if (!status.is_running) {
              clearInterval(pollInterval);
              setBulkParsing(false);
              
              if (status.completed > 0) {
                toast.success(
                  `✅ Power Parse Complete!\n${status.completed} parsed successfully (WhatsApp sent)\n${status.failed > 0 ? `${status.failed} failed` : ''}`,
                  { duration: 5000 }
                );
                loadCandidates();
              } else if (status.failed > 0) {
                toast.error(`Power Parse failed for all ${status.failed} candidate(s). Check console for errors.`);
              }
              
              setSelectedIds([]);
              setTimeout(() => setBulkParseStatus(null), 3000);
            }
          } catch (e) {
            console.error('Status poll error:', e);
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Bulk Power Parse error:', error);
      toast.error(error.response?.data?.detail || 'Bulk Power Parse failed to start');
      setBulkParsing(false);
      setBulkParseStatus(null);
    }
  };

  const addSkill = (skill) => {
    if (!selectedSkills.includes(skill)) {
      setSelectedSkills([...selectedSkills, skill]);
      setPage(1);
    }
    setSkillSearch('');
  };

  const removeSkill = (skill) => {
    setSelectedSkills(selectedSkills.filter(s => s !== skill));
    setPage(1);
  };

  // Quick Share functions
  const openQuickShare = async (candidate) => {
    setQuickShareCandidate(candidate);
    setQuickShareOpen(true);
    setSelectedJobsForShare([]);
    setHrEmailOverride('');
    setLoadingJobs(true);
    
    try {
      // Load jobs that match this candidate
      const matchData = candidateMatches[candidate.id];
      if (matchData?.matches) {
        // Get job details for matches
        const jobsRes = await jobsApi.list({ status: 'open', limit: 100 });
        const matchedJobIds = matchData.matches.map(m => m.job_id);
        const relevantJobs = jobsRes.data.jobs.filter(j => matchedJobIds.includes(j.id))
          .map(j => {
            const match = matchData.matches.find(m => m.job_id === j.id);
            return { ...j, match_score: match?.overall_score || 0, match_id: match?.id };
          })
          .sort((a, b) => b.match_score - a.match_score);
        setQuickShareJobs(relevantJobs);
      } else {
        // Just load open jobs
        const jobsRes = await jobsApi.list({ status: 'open', limit: 50 });
        setQuickShareJobs(jobsRes.data.jobs.map(j => ({ ...j, match_score: 0 })));
      }
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const toggleJobSelection = (jobId) => {
    if (selectedJobsForShare.includes(jobId)) {
      setSelectedJobsForShare(selectedJobsForShare.filter(id => id !== jobId));
    } else {
      setSelectedJobsForShare([...selectedJobsForShare, jobId]);
    }
  };

  const handleQuickShare = async () => {
    if (selectedJobsForShare.length === 0) {
      toast.error('Please select at least one job');
      return;
    }
    
    setQuickSharing(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (const jobId of selectedJobsForShare) {
        const job = quickShareJobs.find(j => j.id === jobId);
        if (!job) continue;
        
        // Find the match for this job
        const matchData = candidateMatches[quickShareCandidate.id];
        const match = matchData?.matches?.find(m => m.job_id === jobId);
        
        if (!match) {
          errorCount++;
          continue;
        }
        
        // Determine HR email
        const hrEmail = hrEmailOverride || job.hr_email || job.company_hr_email;
        if (!hrEmail) {
          toast.warning(`No HR email for ${job.title} - skipped`);
          continue;
        }
        
        try {
          await hrPipelineApi.share(match.id, hrEmail, '');
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Shared with ${successCount} HR contact(s)`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} share(s) failed`);
      }
      
      setQuickShareOpen(false);
    } catch (err) {
      toast.error('Quick share failed');
    } finally {
      setQuickSharing(false);
    }
  };

  // Bulk Quick Share functions
  const openBulkShare = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    
    setBulkShareOpen(true);
    setSelectedJobForBulkShare(null);
    setBulkShareHrEmail('');
    setBulkShareHrName('');
    setLoadingBulkJobs(true);
    
    try {
      // Load all open jobs
      const jobsRes = await jobsApi.list({ status: 'open', limit: 100 });
      const jobs = jobsRes.data.jobs || [];
      
      // Add company HR email info to each job if available
      const jobsWithHr = await Promise.all(jobs.map(async (job) => {
        let hrEmail = job.hr_email || job.company_hr_email || '';
        let hrName = job.hr_name || '';
        
        // Try to get HR contact from company if not on job
        if (!hrEmail && job.company_id) {
          try {
            const companies = await import('../lib/api').then(m => m.companiesApi);
            const companyRes = await companies.get(job.company_id);
            const company = companyRes.data;
            hrEmail = company?.hr_email || company?.contact_email || '';
            hrName = company?.hr_name || company?.contact_name || '';
          } catch (e) {
            // ignore
          }
        }
        
        return { ...job, hr_email: hrEmail, hr_name: hrName };
      }));
      
      setBulkShareJobs(jobsWithHr);
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoadingBulkJobs(false);
    }
  };

  const handleBulkShare = async () => {
    if (!selectedJobForBulkShare) {
      toast.error('Please select a job');
      return;
    }
    
    const hrEmail = bulkShareHrEmail || selectedJobForBulkShare.hr_email;
    if (!hrEmail) {
      toast.error('Please enter an HR email address');
      return;
    }
    
    setBulkSharing(true);
    
    try {
      const hrName = bulkShareHrName || selectedJobForBulkShare.hr_name || '';
      const response = await hrPipelineApi.bulkShare(
        selectedJobForBulkShare.id,
        selectedIds,
        hrEmail,
        hrName
      );
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully shared ${result.success_count} candidate(s) with HR`);
      }
      if (result.skipped_count > 0) {
        toast.info(`${result.skipped_count} candidate(s) were already shared`);
      }
      if (result.failed_count > 0) {
        toast.error(`Failed to share ${result.failed_count} candidate(s)`);
      }
      
      setBulkShareOpen(false);
      setSelectedIds([]);
    } catch (err) {
      toast.error('Bulk share failed');
    } finally {
      setBulkSharing(false);
    }
  };

  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter('');
    setVerificationFilter('');
    setSelectedSkills([]);
    setMinExperience('');
    setMaxExperience('');
    setLocationFilter('');
    setFreshnessFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || statusFilter || verificationFilter || selectedSkills.length > 0 || 
    minExperience || maxExperience || locationFilter || freshnessFilter;

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header 
        title="Candidates" 
        subtitle={`${total} total candidates`}
      >
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-cv-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload CVs
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload CVs</DialogTitle>
            </DialogHeader>
            <FileUpload
              accept=".pdf,.doc,.docx,.txt,.rtf"
              multiple
              onUpload={handleUpload}
              onComplete={handleUploadComplete}
            />
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={() => setBulkUploadOpen(true)} data-testid="bulk-upload-btn">
          <FolderOpen className="w-4 h-4 mr-2" />
          Bulk Upload (100)
        </Button>
        <BulkUploadDialog
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          onComplete={(completed, failed) => {
            if (completed > 0) {
              toast.success(`${completed} CVs processed${failed > 0 ? `, ${failed} failed` : ''}`);
              triggerRefresh(DATA_EVENTS.CANDIDATE_CREATED, { count: completed });
              loadCandidates();
            }
          }}
        />
        <Button variant="outline" onClick={() => navigate('/ats/candidates/new')} data-testid="create-candidate-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Manual
        </Button>
      </Header>

      <div className="page-container">
        {/* Filters Row */}
        <div className="bg-white rounded-lg border p-4 mb-4 space-y-4">
          {/* Top Row - Search and Quick Filters */}
          <div className="flex flex-wrap gap-3 items-center max-md:flex-col max-md:items-stretch">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((v) => !v)}
              className="md:hidden order-2 inline-flex items-center justify-center gap-2 min-h-[44px] w-full rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
              aria-expanded={mobileFiltersOpen}
              data-testid="cand-mobile-filters-toggle"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>
            <div className="relative flex-1 min-w-[200px] max-w-md max-md:order-1 max-md:max-w-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, skills..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
                data-testid="search-candidates"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className={`w-36 max-md:order-3 max-md:w-full max-md:h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="screening">Screening</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="offered">Offered</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Select value={verificationFilter} onValueChange={(v) => { setVerificationFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className={`w-40 max-md:order-4 max-md:w-full max-md:h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`}>
                <SelectValue placeholder="Verification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ai_parsed">AI Parsed</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
              </SelectContent>
            </Select>

            <Select value={freshnessFilter} onValueChange={(v) => { setFreshnessFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className={`w-36 max-md:order-5 max-md:w-full max-md:h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`}>
                <SelectValue placeholder="Freshness" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 days</SelectItem>
                <SelectItem value="month">Last 30 days</SelectItem>
                <SelectItem value="quarter">Last 90 days</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant={showAdvancedSearch ? "secondary" : "outline"} 
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className={`gap-2 max-md:order-6 max-md:w-full max-md:min-h-[44px] ${mobileFiltersOpen ? '' : 'max-md:hidden'}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              More Filters
              {showAdvancedSearch ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          {/* Advanced Filters */}
          {showAdvancedSearch && (
            <div className="pt-4 border-t grid gap-4 md:grid-cols-4">
              {/* Skills Search */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Skills</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Type to search skills..."
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    className="pl-10"
                  />
                  {skillSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {availableSkills
                        .filter(s => s.toLowerCase().includes(skillSearch.toLowerCase()))
                        .filter(s => !selectedSkills.includes(s))
                        .slice(0, 10)
                        .map(skill => (
                          <button
                            key={skill}
                            onClick={() => addSkill(skill)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100"
                          >
                            {skill}
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>
                {selectedSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedSkills.map(skill => (
                      <Badge key={skill} variant="secondary" className="cursor-pointer" onClick={() => removeSkill(skill)}>
                        {skill} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Experience Range */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Experience (Years)</label>
                <div className="flex gap-2 items-center">
                  <Input type="number" placeholder="Min" value={minExperience} onChange={(e) => { setMinExperience(e.target.value); setPage(1); }} className="w-20" min="0" />
                  <span className="text-slate-400">-</span>
                  <Input type="number" placeholder="Max" value={maxExperience} onChange={(e) => { setMaxExperience(e.target.value); setPage(1); }} className="w-20" min="0" />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Location</label>
                <SearchableSelect
                  value={locationFilter}
                  onValueChange={(v) => { setLocationFilter(v); setPage(1); }}
                  options={filterOptions.locations.map(loc => ({ id: loc, name: loc }))}
                  placeholder="All Locations"
                  searchPlaceholder="Search locations..."
                  emptyMessage="No locations found"
                  displayKey="name"
                  valueKey="id"
                  data-testid="filter-location"
                />
              </div>
            </div>
          )}
        </div>

        {/* List Controls */}
        <div className="flex items-center justify-between mb-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
          <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch">
            {selectedIds.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap [&>button]:max-md:min-h-[44px] [&>button]:max-md:flex-1">
                <Badge variant="secondary">{selectedIds.length} selected</Badge>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openBulkShare}
                  className="bg-blue-500 text-white border-0 hover:bg-blue-600"
                  data-testid="bulk-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share with HR
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBulkPowerMatch}
                  disabled={bulkMatching}
                  className="bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600"
                >
                  {bulkMatching ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-1" />
                  )}
                  Power Match
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBulkPowerParse}
                  disabled={bulkParsing}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 hover:from-amber-600 hover:to-orange-600"
                  data-testid="bulk-power-parse-btn"
                >
                  {bulkParsing ? (
                    <div className="flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="max-w-[120px] truncate">
                        {bulkParseStatus?.current_item 
                          ? `${bulkParseStatus.completed}/${bulkParseStatus.total}: ${bulkParseStatus.current_item}` 
                          : `${bulkParseStatus?.percentage || 0}%`
                        }
                      </span>
                    </div>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Power Parse
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddToRequirementOpen(true)}
                  className="bg-emerald-600 text-white border-0 hover:bg-emerald-700"
                  data-testid="bulk-add-to-requirement-btn"
                >
                  <Briefcase className="w-4 h-4 mr-1" />
                  Add to Requirement
                </Button>
                <AddToRequirementDialog
                  open={addToRequirementOpen}
                  onOpenChange={(o) => { setAddToRequirementOpen(o); if (!o && addToRequirementDone) { setSelectedIds([]); setAddToRequirementDone(false); } }}
                  mode="candidate"
                  candidateIds={selectedIds}
                  candidateNames={selectedIds.map((sid) => (candidates.find((c) => c.id === sid) || {}).name).filter(Boolean)}
                  onAdded={(res) => { if (res && res.added > 0) setAddToRequirementDone(true); }}
                />
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                  Clear Selection
                </Button>
              </div>
            ) : (
              <span className="text-sm text-slate-500">
                Showing {candidates.length} of {total} candidates
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 max-md:grid max-md:grid-cols-2">
            {/* Sort */}
            <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => { const [field, order] = v.split('-'); setSortBy(field); setSortOrder(order); }}>
              <SelectTrigger className="w-52 max-md:w-full max-md:h-[44px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at-desc">Newest First</SelectItem>
                <SelectItem value="created_at-asc">Oldest First</SelectItem>
                <SelectItem value="match_score-desc">Best Match First</SelectItem>
                <SelectItem value="match_score-asc">Lowest Match First</SelectItem>
                <SelectItem value="placement_probability-desc">Highest Probability First</SelectItem>
                <SelectItem value="placement_probability-asc">Lowest Probability First</SelectItem>
                <SelectItem value="match_count-desc">Most Opportunities First</SelectItem>
                <SelectItem value="match_count-asc">Least Opportunities First</SelectItem>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
              </SelectContent>
            </Select>

            {/* Items per page */}
            <Select value={String(itemsPerPage)} onValueChange={(v) => { setItemsPerPage(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-28 max-md:w-full max-md:h-[44px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem key={size} value={String(size)}>{size} / page</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Candidates List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : candidates.length === 0 ? (
          <Card className="py-16">
            <CardContent className="text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700">No candidates found</h3>
              <p className="text-slate-500 mt-1">Try adjusting your filters or upload new CVs</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            {/* Header Row */}
            <div className="flex items-center gap-4 px-4 py-3 bg-slate-50 border-b font-medium text-sm text-slate-600 max-md:hidden">
              <div className="w-8">
                <Checkbox 
                  checked={selectedIds.length === candidates.length && candidates.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </div>
              <div className="flex-1">Candidate</div>
              <div className="w-32 text-center">Opportunities</div>
              <div className="w-24 text-center">Best Match</div>
              <div className="w-24 text-center">Probability</div>
              <div className="w-28 text-center">Status</div>
              <div className="w-28 text-center">Freshness</div>
              <div className="w-32 text-center">Actions</div>
            </div>

            {/* Candidate Rows */}
            {candidates.map((candidate) => {
              const isExpanded = expandedIds.includes(candidate.id);
              const isSelected = selectedIds.includes(candidate.id);
              const freshness = getFreshness(candidate.created_at);
              
              return (
                <Collapsible key={candidate.id} open={isExpanded} onOpenChange={() => toggleExpand(candidate.id)}>
                  {/* Main Row */}
                  <div className={`flex items-center gap-4 px-4 py-3 border-b hover:bg-slate-50 transition-colors max-md:flex-wrap max-md:items-start max-md:gap-x-2 max-md:gap-y-2 max-md:px-3 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <div className="w-8 max-md:w-6 max-md:pt-2">
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(candidate.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    
                    <CollapsibleTrigger className="flex-1 flex items-center gap-3 text-left cursor-pointer max-md:basis-[calc(100%-2.5rem)] max-md:min-w-0">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      
                      {/* Name & Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate">{candidate.name || 'Unknown'}</span>
                          {candidate.verification_status === 'verified' ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Bot className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          )}
                          {(candidate.source === 'public_submission' || candidate.source === 'portal') && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200 flex-shrink-0"
                              title="Uploaded via Candidate Portal"
                              data-testid="portal-badge"
                            >
                              <Globe className="w-3 h-3" />
                              Portal
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5 max-md:flex-wrap max-md:gap-x-2">
                          {candidate.current_designation && (
                            <span className="truncate max-w-[200px] max-md:max-w-full">{candidate.current_designation}</span>
                          )}
                          {candidate.current_location && (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <MapPin className="w-3 h-3" />
                              {candidate.current_location}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expand Icon */}
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </CollapsibleTrigger>

                    {/* Opportunities - JD Matches & Companies */}
                    <div className="w-32 text-center max-md:w-auto max-md:min-w-0">
                      {candidateMatches[candidate.id]?.totalJdMatches > 0 ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-sm font-semibold text-blue-600">
                            {candidateMatches[candidate.id].totalJdMatches} JD{candidateMatches[candidate.id].totalJdMatches !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-slate-500">
                            {candidateMatches[candidate.id].uniqueCompanyCount} compan{candidateMatches[candidate.id].uniqueCompanyCount !== 1 ? 'ies' : 'y'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">No matches</span>
                      )}
                    </div>

                    {/* Best Match Score */}
                    <div className="w-24 text-center max-md:w-auto">
                      {candidateMatches[candidate.id]?.highest ? (
                        <Badge 
                          className={`text-xs font-semibold ${
                            candidateMatches[candidate.id].highest >= 80 
                              ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                              : candidateMatches[candidate.id].highest >= 60 
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' 
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {Math.round(candidateMatches[candidate.id].highest)}%
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </div>

                    {/* Placement Probability */}
                    <div className="w-24 text-center max-md:w-auto">
                      {candidate.placement_probability !== undefined ? (
                        <Badge 
                          className={`text-xs font-semibold ${
                            candidate.placement_probability >= 70 
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' 
                              : candidate.placement_probability >= 50 
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' 
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                          }`}
                          title={candidate.placement_factors?.join(', ') || 'No factors'}
                        >
                          <Zap className="w-3 h-3 mr-1" />
                          {Math.round(candidate.placement_probability)}%
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="w-28 text-center max-md:w-auto">
                      <StatusBadge status={candidate.status || 'new'} />
                    </div>

                    {/* Freshness */}
                    <div className="w-28 text-center max-md:w-auto">
                      <Badge className={`${freshness.color} text-xs`}>
                        <Clock className="w-3 h-3 mr-1" />
                        {freshness.label}
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="w-44 flex items-center justify-center gap-1 max-md:w-full max-md:justify-start max-md:flex-wrap max-md:border-t max-md:pt-2 [&_button]:max-md:min-h-[44px] [&_button]:max-md:min-w-[44px]">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); setPeekCandidate(candidate); setPeekOpen(true); }}
                        title="Quick Peek"
                        data-testid="peek-btn"
                      >
                        <PanelRightOpen className="w-4 h-4 text-purple-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); openQuickShare(candidate); }}
                        title="Quick Share with HR"
                      >
                        <Share2 className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/ats/candidates/${candidate.id}`}>
                          <Eye className="w-4 h-4" />
                        </Link>
                      </Button>
                      {/* Login as Candidate - opens portal in new tab */}
                      {candidate.portal_slug && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => handleOpenCandidatePortal(candidate, e)}
                          title="Open Candidate Portal"
                          data-testid={`portal-btn-${candidate.id}`}
                        >
                          <LogIn className="w-4 h-4 text-violet-500" />
                        </Button>
                      )}
                      {/* Password Reset for Candidate */}
                      {candidate.portal_slug && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); setResetPasswordDialog(candidate); }}
                          title="Send Password Reset"
                          data-testid={`reset-pwd-btn-${candidate.id}`}
                        >
                          <KeyRound className="w-4 h-4 text-amber-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={(e) => handleDelete(candidate.id, e)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <CollapsibleContent>
                    <div className="px-4 py-4 bg-slate-50 border-b">
                      {/* Quick Stats Row */}
                      <div className="flex flex-wrap gap-4 ml-12 mb-4 max-md:ml-0 max-md:gap-2">
                        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border">
                          <Briefcase className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-slate-700">
                            {(candidate.work_experience?.length || 0)} Companies
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border">
                          <Clock className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium text-slate-700">
                            {candidate.total_experience_years || candidate.total_years_experience || 0} Years Exp.
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-slate-700">
                            {(() => {
                              const skills = candidate.technical_skills || candidate.skills || [];
                              const tools = candidate.tools_technologies || [];
                              const langs = candidate.programming_languages || [];
                              return [...new Set([...skills, ...tools, ...langs])].length;
                            })()} Skills
                          </span>
                        </div>
                        {candidate.placement_probability !== undefined && (
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                            candidate.placement_probability >= 70 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : candidate.placement_probability >= 50 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'bg-white'
                          }`}>
                            <Zap className={`w-4 h-4 ${
                              candidate.placement_probability >= 70 
                                ? 'text-emerald-500' 
                                : candidate.placement_probability >= 50 
                                  ? 'text-blue-500' 
                                  : 'text-slate-400'
                            }`} />
                            <span className={`text-sm font-medium ${
                              candidate.placement_probability >= 70 
                                ? 'text-emerald-700' 
                                : candidate.placement_probability >= 50 
                                  ? 'text-blue-700' 
                                  : 'text-slate-700'
                            }`}>
                              {Math.round(candidate.placement_probability)}% Probability
                            </span>
                          </div>
                        )}
                        {candidateMatches[candidate.id]?.above70?.length > 0 && (
                          <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">
                              {candidateMatches[candidate.id].above70.length} Strong Match{candidateMatches[candidate.id].above70.length !== 1 ? 'es' : ''}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid md:grid-cols-4 gap-6 ml-12 max-md:ml-0 max-md:gap-4">
                        {/* Contact Info */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-slate-700 dark:text-slate-300 text-sm">Contact</h4>
                          {candidate.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <a href={`mailto:${candidate.email}`} className="hover:text-blue-600 truncate max-w-[180px]">{candidate.email}</a>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <a href={`tel:${candidate.phone}`} className="hover:text-blue-600">{candidate.phone}</a>
                            </div>
                          )}
                          {candidate.current_location && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              {candidate.current_location}
                            </div>
                          )}
                        </div>

                        {/* Professional Info */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-slate-700 dark:text-slate-300 text-sm">Professional</h4>
                          {candidate.current_company && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="truncate max-w-[180px]">{candidate.current_company}</span>
                            </div>
                          )}
                          {candidate.current_industry && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Briefcase className="w-4 h-4 text-slate-400" />
                              <span className="truncate max-w-[180px]">{candidate.current_industry}</span>
                            </div>
                          )}
                          {candidate.career_level && (
                            <div className="flex items-center gap-2 text-sm">
                              <Badge variant="outline" className="text-xs capitalize">{candidate.career_level}</Badge>
                            </div>
                          )}
                          {(candidate.expected_salary || candidate.current_salary) && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <DollarSign className="w-4 h-4 text-slate-400" />
                              {candidate.expected_salary ? `Exp: ${candidate.expected_salary}` : `Curr: ${candidate.current_salary}`}
                            </div>
                          )}
                          {(candidate.notice_period || candidate.notice_period_days) && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              Notice: {candidate.notice_period || `${candidate.notice_period_days} days`}
                            </div>
                          )}
                        </div>

                        {/* Skills Preview */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-slate-700 dark:text-slate-300 text-sm">Technical Skills</h4>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              const skills = candidate.technical_skills || candidate.skills || [];
                              const skillsArray = typeof skills === 'string' ? skills.split(',') : skills;
                              return skillsArray.slice(0, 5).map((skill, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {typeof skill === 'string' ? skill.trim() : (skill?.name || JSON.stringify(skill))}
                                </Badge>
                              ));
                            })()}
                            {(() => {
                              const skills = candidate.technical_skills || candidate.skills || [];
                              const count = typeof skills === 'string' ? skills.split(',').length : skills.length;
                              return count > 5 && (
                                <Badge variant="outline" className="text-xs">+{count - 5} more</Badge>
                              );
                            })()}
                          </div>
                          {/* Cloud Platforms */}
                          {candidate.cloud_platforms?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Cloud:</p>
                              <div className="flex flex-wrap gap-1">
                                {candidate.cloud_platforms.slice(0, 3).map((platform, i) => (
                                  <Badge key={i} className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                                    {platform}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Additional Info */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-slate-700 dark:text-slate-300 text-sm">Additional</h4>
                          {/* Education Preview */}
                          {candidate.education?.length > 0 && (
                            <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <GraduationCap className="w-4 h-4 text-slate-400 mt-0.5" />
                              <div className="truncate max-w-[180px]">
                                {candidate.education[0]?.degree_type || candidate.education[0]?.degree}
                                {candidate.education[0]?.institution_name && (
                                  <span className="block text-xs text-slate-500">{candidate.education[0].institution_name}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Certifications count */}
                          {candidate.certifications?.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30">
                                {candidate.certifications.length} Certification{candidate.certifications.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          )}
                          {/* Languages */}
                          {(candidate.languages_spoken?.length > 0 || candidate.language_proficiency?.length > 0) && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <Badge variant="outline" className="text-xs">
                                {(() => {
                                  const langs = candidate.languages_spoken || candidate.language_proficiency || [];
                                  return langs.slice(0, 2).map(l => typeof l === 'string' ? l : l?.name || l).join(', ') + 
                                    (langs.length > 2 ? ` +${langs.length - 2}` : '');
                                })()}
                              </Badge>
                            </div>
                          )}
                          {/* Awards */}
                          {candidate.awards_recognition?.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                              <Sparkles className="w-4 h-4" />
                              {candidate.awards_recognition.length} Award{candidate.awards_recognition.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Job Matches Section (>70%) */}
                      {candidateMatches[candidate.id]?.above70?.length > 0 && (
                        <div className="mt-4 ml-12">
                          <h4 className="font-medium text-slate-700 text-sm mb-2 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-green-600" />
                            Strong Job Matches ({'>'}70%)
                          </h4>
                          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {candidateMatches[candidate.id].above70.slice(0, 6).map((match, idx) => (
                              <div 
                                key={idx} 
                                className="flex items-center justify-between bg-white p-2.5 rounded-lg border hover:border-green-300 transition-colors"
                              >
                                <div className="flex-1 min-w-0 mr-2">
                                  <p className="text-sm font-medium text-slate-800 truncate">
                                    {match.job_title || 'Job'}
                                  </p>
                                  {match.company_name && (
                                    <p className="text-xs text-slate-500 truncate">
                                      {match.company_name}
                                    </p>
                                  )}
                                </div>
                                <Badge 
                                  className={`text-xs font-semibold flex-shrink-0 ${
                                    match.score >= 85 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-emerald-100 text-emerald-700'
                                  }`}
                                >
                                  {Math.round(match.score)}%
                                </Badge>
                              </div>
                            ))}
                          </div>
                          {candidateMatches[candidate.id].above70.length > 6 && (
                            <p className="text-xs text-slate-500 mt-2">
                              +{candidateMatches[candidate.id].above70.length - 6} more strong matches
                            </p>
                          )}
                        </div>
                      )}

                      {/* View Full Profile Button */}
                      <div className="mt-4 ml-12">
                        <Button size="sm" asChild>
                          <Link to={`/ats/candidates/${candidate.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Full Profile
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Page {page} of {totalPages}
            </div>
            <Pagination 
              currentPage={page} 
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              emptyLabel="No candidates match these filters"
            />
          </div>
        )}
      </div>

      {/* Quick Share Modal */}
      <Dialog open={quickShareOpen} onOpenChange={setQuickShareOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-500" />
              Quick Share: {quickShareCandidate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            <p className="text-sm text-slate-600">
              Select jobs to share this candidate with their company HR contacts.
            </p>
            
            <div>
              <label className="text-sm text-slate-600 block mb-1">Override HR Email (optional)</label>
              <Input
                type="email"
                value={hrEmailOverride}
                onChange={(e) => setHrEmailOverride(e.target.value)}
                placeholder="Leave empty to use company HR email"
              />
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 border-b">
                <span className="text-sm font-medium text-slate-700">
                  Matching Jobs ({quickShareJobs.length})
                </span>
              </div>
              
              {loadingJobs ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : quickShareJobs.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No matching jobs found</p>
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {quickShareJobs.map((job) => (
                    <div 
                      key={job.id}
                      className={`flex items-center gap-3 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 ${
                        selectedJobsForShare.includes(job.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleJobSelection(job.id)}
                    >
                      <Checkbox
                        checked={selectedJobsForShare.includes(job.id)}
                        onCheckedChange={() => toggleJobSelection(job.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">{job.title}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {job.company_name || 'No company'} • {job.location || 'Remote'}
                        </p>
                      </div>
                      {job.match_score > 0 && (
                        <Badge className={`text-xs ${
                          job.match_score >= 70 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {Math.round(job.match_score)}%
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {selectedJobsForShare.length > 0 && (
              <p className="text-sm text-blue-600">
                {selectedJobsForShare.length} job(s) selected
              </p>
            )}
          </div>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setQuickShareOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleQuickShare} 
              disabled={quickSharing || selectedJobsForShare.length === 0}
            >
              {quickSharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Share with HR ({selectedJobsForShare.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Quick Share Modal */}
      <Dialog open={bulkShareOpen} onOpenChange={setBulkShareOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="bulk-share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-500" />
              Share {selectedIds.length} Candidate{selectedIds.length > 1 ? 's' : ''} with HR
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            <p className="text-sm text-slate-600">
              Select a job position and HR contact to share all selected candidates.
            </p>
            
            {/* Job Selection */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 border-b">
                <span className="text-sm font-medium text-slate-700">
                  Select Job Position
                </span>
              </div>
              
              {loadingBulkJobs ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : bulkShareJobs.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No open jobs found</p>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  {bulkShareJobs.map((job) => (
                    <div 
                      key={job.id}
                      className={`flex items-center gap-3 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 ${
                        selectedJobForBulkShare?.id === job.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => {
                        setSelectedJobForBulkShare(job);
                        if (job.hr_email) {
                          setBulkShareHrEmail(job.hr_email);
                          setBulkShareHrName(job.hr_name || '');
                        }
                      }}
                      data-testid={`job-option-${job.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">{job.title}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {job.company_name || 'No company'} • {job.location || 'Remote'}
                        </p>
                        {job.hr_email && (
                          <p className="text-xs text-blue-600 truncate flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" />
                            HR: {job.hr_email}
                          </p>
                        )}
                      </div>
                      {selectedJobForBulkShare?.id === job.id && (
                        <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* HR Contact Info */}
            {selectedJobForBulkShare && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700">HR Contact Details</p>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">HR Email *</label>
                  <Input
                    type="email"
                    value={bulkShareHrEmail}
                    onChange={(e) => setBulkShareHrEmail(e.target.value)}
                    placeholder="hr@company.com"
                    data-testid="bulk-share-hr-email"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">HR Name (optional)</label>
                  <Input
                    value={bulkShareHrName}
                    onChange={(e) => setBulkShareHrName(e.target.value)}
                    placeholder="HR Manager"
                    data-testid="bulk-share-hr-name"
                  />
                </div>
              </div>
            )}
            
            {/* Selected Candidates Summary */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Candidates to share ({selectedIds.length})
              </p>
              <div className="flex flex-wrap gap-1">
                {candidates
                  .filter(c => selectedIds.includes(c.id))
                  .slice(0, 10)
                  .map(c => (
                    <Badge key={c.id} variant="secondary" className="text-xs">
                      {c.name}
                    </Badge>
                  ))}
                {selectedIds.length > 10 && (
                  <Badge variant="secondary" className="text-xs">
                    +{selectedIds.length - 10} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setBulkShareOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkShare} 
              disabled={bulkSharing || !selectedJobForBulkShare || !bulkShareHrEmail}
              data-testid="bulk-share-submit"
            >
              {bulkSharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Share {selectedIds.length} Candidate{selectedIds.length > 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Candidate Peek Panel */}
      <CandidatePeekPanel
        candidate={peekCandidate}
        open={peekOpen}
        onClose={() => setPeekOpen(false)}
        onUpdate={loadCandidates}
      />

      {/* Candidate Password Reset Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={() => setResetPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Password Reset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Send a password reset link to <strong>{resetPasswordDialog?.name}</strong> for their candidate portal
            </p>
            <div>
              <Label>Send via</Label>
              <Select value={resetMethod} onValueChange={setResetMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span>Email ({resetPasswordDialog?.email || 'not set'})</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="whatsapp">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>WhatsApp ({resetPasswordDialog?.phone || 'not set'})</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog(null)}>Cancel</Button>
            <Button onClick={handleSendCandidatePasswordReset} disabled={sendingReset}>
              {sendingReset ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Send Reset Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
